import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Settings2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useInteractionTemplates, DEFAULT_TEMPLATES } from '@/hooks/use-interaction-templates'
import { QuickLogPopup } from '@/components/QuickLogPopup'
import type { InteractionType, InteractionQuality } from '@/lib/types'

interface QuickLogChipsProps {
  contactId: string
  onManageTemplates?: () => void
}

export function QuickLogChips({ contactId, onManageTemplates }: QuickLogChipsProps) {
  const { t } = useTranslation()
  const { data: customTemplatesData } = useInteractionTemplates()

  const customTemplates = customTemplatesData?.items ?? []

  // Merge: custom templates first (user-defined order), then default presets
  const allTemplates = [
    ...customTemplates.map((ct) => ({
      id: ct.id,
      name: ct.name,
      type: ct.type as InteractionType,
      quality: ct.quality as InteractionQuality | null,
      icon: ct.icon,
      isPreset: false as const,
    })),
    ...DEFAULT_TEMPLATES,
  ]

  // Popup state
  const [popupTemplate, setPopupTemplate] = useState<{
    id: string
    name: string
    type: InteractionType
    quality: InteractionQuality | null
    icon: string | null
  } | null>(null)

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-on-surface-variant text-xs font-medium tracking-wider uppercase">
          {t('quickLog.title', 'Quick Log')}
        </h3>
        {onManageTemplates && (
          <button
            onClick={onManageTemplates}
            className="text-on-surface-variant hover:text-foreground p-1 transition-colors"
            title={t('quickLog.manage', 'Manage templates')}
          >
            <Settings2 className="size-3.5" />
          </button>
        )}
      </div>
      <div className="flex scrollbar-none gap-2 overflow-x-auto pb-1">
        {allTemplates.map((template) => (
          <button
            key={template.id}
            onClick={() => setPopupTemplate(template)}
            className={cn(
              'bg-surface-container hover:bg-surface-container-high border-outline-variant/20 inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all',
              'active:scale-95',
            )}
          >
            {template.icon && <span className="text-sm">{template.icon}</span>}
            <span>{template.name}</span>
          </button>
        ))}
      </div>

      {popupTemplate && (
        <QuickLogPopup
          contactId={contactId}
          template={popupTemplate}
          onClose={() => setPopupTemplate(null)}
          onSaved={() => setPopupTemplate(null)}
        />
      )}
    </div>
  )
}
